import {InterfaceEntity, InterfaceEntityTypeEnum, interfaceService} from '@store/settings/interface';
import ModalComponent from '@components/modal/Modal.component';
import {Stack} from '@mui/material';
import SelectComponent from '@components/inputs/select/Select.component';
import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import AsyncSelectWithSearchComponent from '@components/inputs/asyncSelect/AsyncSelectWithSearch.component';
import {FilterBaseEntityEnum, filtersService} from '@store/filters';
import {TableFilterType} from '@store/common/tableFilter.model';
import {map, of} from 'rxjs';
import NumberTextFieldComponent from '@components/inputs/numberTextField/NumberTextField.component';
import TextFieldComponent from '@components/inputs/textField/TextField.component';
import {useSnackbar} from 'notistack';
import {LoadingButton} from '@mui/lab';

interface EditInterfaceEntityModalProps {
  entityType: InterfaceEntityTypeEnum;
  entity: InterfaceEntity;
  handleClose: () => void;
}

const EditInterfaceEntityModal = (props: EditInterfaceEntityModalProps) => {
  const {entity, entityType, handleClose} = props;

  const {enqueueSnackbar} = useSnackbar();

  const [interfaceEntity, setInterfaceEntity] = React.useState<InterfaceEntity>({
    ...entity,
    entityType,
    entityItem: {value: '', label: ''}
  });
  const [saveLoading, setSaveLoading] = useState(false);

  const {t} = useTranslation();

  const handleSave = () => {
    if (entity.id === -1) {
      setSaveLoading(true);
      interfaceService.createInterfaceEntity(interfaceEntity).subscribe({
        next: () => {
          enqueueSnackbar(t('settings.interfaces.modal.success'), {variant: 'success'});
          handleClose();
        },
        error: (err) => {
          enqueueSnackbar(err.text, err.options);
          setSaveLoading(false);
        },
      });
    }
  };

  const updateInterfaceEntity = (e: Partial<InterfaceEntity>) => {
    setInterfaceEntity({
      ...interfaceEntity,
      ...e
    });
  };

  return <ModalComponent
    title={t('settings.interfaces.modal.title')}
    handleClose={handleClose}
    content={
      <Stack width="70%" spacing={2} pt="55px" pb="10px">
        <SelectComponent
          color="secondary"
          items={InterfaceEntityTypeEnum.selectItems().filter((e) => ![InterfaceEntityTypeEnum.CONTRACT, InterfaceEntityTypeEnum.SERVICE].includes(e.value))}
          value={interfaceEntity.entityType}
          handleChange={(entityType) => {
            updateInterfaceEntity({entityType});
          }}
          label={t('settings.interfaces.modal.entityType')}
        />

        <AsyncSelectWithSearchComponent
          color="secondary"
          value={interfaceEntity.entityItem}
          handleChange={(e) => {
            updateInterfaceEntity({entityItem: e, id: e?.value});
          }}
          getOptions={(search) => {
            switch (interfaceEntity.entityType) {
              case InterfaceEntityTypeEnum.ORGANISATION:
                return filtersService.getFilters(TableFilterType.CLIENT_ORGANISATION, FilterBaseEntityEnum.NONE, [], search).pipe(map((e) => e.map((e) => ({
                  label: e.name,
                  value: e.id
                }))));
              case InterfaceEntityTypeEnum.ORGANISATION_UNIT:
                return filtersService.getFilters(TableFilterType.CLIENT_ORGANISATION_UNIT, FilterBaseEntityEnum.NONE, [], search).pipe(map((e) => e.map((e) => ({
                  label: e.name,
                  value: e.id
                }))));
              case InterfaceEntityTypeEnum.SUPPLIER_ORGANISATION_UNIT:
                return filtersService.getFilters(TableFilterType.SUPPLIER_ORGANISATION_UNIT, FilterBaseEntityEnum.NONE, [], search).pipe(map((e) => e.map((e) => ({
                  label: e.name,
                  value: e.id
                }))));
              case InterfaceEntityTypeEnum.WASTE:
                return filtersService.getFilters(TableFilterType.WASTE, FilterBaseEntityEnum.NONE, [], search).pipe(map((e) => e.map((e) => ({
                  label: e.name,
                  value: e.id
                }))));
              case InterfaceEntityTypeEnum.CONTAINER:
                return filtersService.getFilters(TableFilterType.CONTAINER, FilterBaseEntityEnum.NONE, [], search).pipe(map((e) => e.map((e) => ({
                  label: e.name,
                  value: e.id
                }))));
              case InterfaceEntityTypeEnum.SERVICE_KIND:
                return filtersService.getFilters(TableFilterType.SERVICE_KIND, FilterBaseEntityEnum.NONE, [], search).pipe(map((e) => e.map((e) => ({
                  label: e.name,
                  value: e.id
                }))));
              default:
                return of([]);

            }
          }}
          disabled={interfaceEntity.entityTypeItem != null}
          label={t('settings.interfaces.modal.entity')}
        />

        <NumberTextFieldComponent
          color="secondary"
          labelWidth={160}
          label={t('settings.interfaces.modal.metawasteId')}
          placeholder={t('global.writeSomething')}
          disabled={true}
          value={interfaceEntity.id}
        />

        <SelectComponent
          color="secondary"
          items={['NESSY', 'TANGBOM'].map((e) => ({label: e, value: e}))}
          value={interfaceEntity.datahubOrigin}
          handleChange={(v) => {
            updateInterfaceEntity({datahubOrigin: v});
          }}
          label={t('settings.interfaces.modal.datahubOrigin')}
        />
        <TextFieldComponent
          color="secondary"
          labelWidth={160}
          label={t('settings.interfaces.modal.datahubId')}
          placeholder={t('global.writeSomething')}
          handleChange={(v) => {
            updateInterfaceEntity({datahubId: v});
          }}
          value={interfaceEntity.datahubId}
        />

      </Stack>
    }
    actions={
      <Stack direction="row" spacing={2}>
        <LoadingButton
          variant="contained"
          onClick={handleSave}
          loading={saveLoading}
          color="primary">
          {t('global.save')}
        </LoadingButton>
      </Stack>
    }
  />;
};

export default EditInterfaceEntityModal;
